import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { workerFetch } from '../worker';

interface FredPoint  { date: string; value: number; }
interface FredSeries { series: string; data: FredPoint[]; }
interface FredSignal { label: string; severity: string; }
interface FredRisk   { score: number; label: 'GREEN' | 'YELLOW' | 'RED'; signals: FredSignal[]; }
interface FredResp   { indicators: FredSeries[]; risk: FredRisk; cachedAt: string; }

const RISK_COLOR = { GREEN: 'var(--green)', YELLOW: 'var(--warn)', RED: 'var(--red)' };
const SEV_COLOR: Record<string, string> = {
  severe: 'var(--red)', high: 'var(--red)', medium: 'var(--warn)', low: 'var(--text3)',
};

const INDICATORS = [
  {
    series:  'YIELD_CURVE',
    label:   '10Y−2Y Yield Spread',
    unit:    '%',
    desc:    'Inverted yield curve has preceded every recession since 1970. Negative = warning.',
    thresholds: [
      { v: 0,   color: 'var(--red)',   label: 'Inverted' },
      { v: 0.5, color: 'var(--warn)',  label: 'Flat' },
      { v: 999, color: 'var(--green)', label: 'Normal' },
    ],
    newestFirst: true,
  },
  {
    series:  'UNRATE',
    label:   'Unemployment Rate',
    unit:    '%',
    desc:    'Rising 3-month trend signals labor market deterioration',
    thresholds: [
      { v: 5.5, color: 'var(--red)',   label: 'Elevated' },
      { v: 4.5, color: 'var(--warn)',  label: 'Rising' },
      { v: 0,   color: 'var(--green)', label: 'Healthy' },
    ],
    newestFirst: true,
  },
  {
    series:  'FEDFUNDS',
    label:   'Federal Funds Rate',
    unit:    '%',
    desc:    'Fed policy rate — rapid rises tighten credit and slow the economy',
    thresholds: [
      { v: 5,   color: 'var(--red)',   label: 'Restrictive' },
      { v: 3,   color: 'var(--warn)',  label: 'Elevated' },
      { v: 0,   color: 'var(--green)', label: 'Accommodative' },
    ],
    newestFirst: true,
  },
  {
    series:  'YIELD_10Y',
    label:   '10-Year Treasury Yield',
    unit:    '%',
    desc:    'Long-end benchmark rate — rising rate can signal tightening financial conditions',
    thresholds: [
      { v: 5,   color: 'var(--warn)',  label: 'Elevated' },
      { v: 0,   color: 'var(--green)', label: 'Normal' },
    ],
    newestFirst: true,
  },
];

// AV economic endpoints return newest-first
function latest(data: FredPoint[]): number | null {
  return data.length ? data[0].value : null;
}

function nBack(data: FredPoint[], n: number): number | null {
  return data.length >= n ? data[n - 1].value : null;
}

// For sparkline we need oldest-first
function chronological(data: FredPoint[]): FredPoint[] {
  return [...data].reverse();
}

function indicatorColor(series: string, value: number): string {
  const ind = INDICATORS.find(i => i.series === series);
  if (!ind) return 'var(--text3)';
  for (const t of ind.thresholds) {
    if (value >= t.v) return t.color;
  }
  return 'var(--text3)';
}

function formatValue(_series: string, value: number): string {
  return (value >= 0 ? '+' : '') + value.toFixed(2) + '%';
}

function sparkline(data: FredPoint[], color: string, w = 120, h = 36): string {
  const vals = data.slice(-52).map(d => d.value);
  if (vals.length < 2) return '';
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / range) * (h - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const zero = max > 0 && min < 0
    ? `<line x1="0" y1="${(h - ((0 - min) / range) * (h - 4) - 2).toFixed(1)}" x2="${w}" y2="${(h - ((0 - min) / range) * (h - 4) - 2).toFixed(1)}" stroke="var(--border)" stroke-width="1" stroke-dasharray="2,2"/>`
    : '';
  return `<svg width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" style="display:block;overflow:visible;">${zero}<polyline points="${esc(pts)}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linejoin="round"/></svg>`;
}

function trendArrow(curr: number | null, prev: number | null): string {
  if (curr === null || prev === null) return '';
  const diff = curr - prev;
  if (Math.abs(diff) < 0.01) return '<span style="color:var(--text3);">→</span>';
  return diff > 0
    ? `<span style="color:var(--red);">↑ +${Math.abs(diff).toFixed(2)}</span>`
    : `<span style="color:var(--green);">↓ −${Math.abs(diff).toFixed(2)}</span>`;
}

export class FredTab implements Tab {
  readonly id = 'fred';
  private loaded = false;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('fred-refresh-btn').addEventListener('click', () => {
      this.loaded = false;
      this.load();
    });
  }

  render(): void {
    if (!this.loaded) this.load();
  }

  private async load(): Promise<void> {
    this.el('fred-body').innerHTML = `
      <div style="padding:30px;color:var(--text3);font-family:var(--mono);font-size:12px;">
        Loading FRED data…
      </div>`;

    try {
      const data = await workerFetch<FredResp>('/api/fred');
      this.loaded = true;
      this.el('fred-updated').textContent = `Cached ${timeAgo(data.cachedAt)}`;
      this.render_body(data);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.el('fred-body').innerHTML =
        `<div style="padding:20px;color:var(--red);font-family:var(--mono);font-size:12px;">Error: ${esc(msg)}</div>`;
    }
  }

  private render_body(data: FredResp): void {
    const { indicators, risk } = data;
    const riskCol = RISK_COLOR[risk.label];

    const getSeries = (s: string) => indicators.find(i => i.series === s)?.data ?? [];

    // ── Traffic light ─────────────────────────────────────────────────────────
    const signalRows = risk.signals.length
      ? risk.signals.map(s => `
          <div style="display:flex;gap:8px;align-items:center;margin-bottom:4px;">
            <span style="color:${SEV_COLOR[s.severity] ?? 'var(--text3)'};font-size:12px;">⚠</span>
            <span style="font-size:12px;color:var(--text2);">${esc(s.label)}</span>
          </div>`).join('')
      : `<div style="color:var(--green);font-size:12px;">No active warning signals.</div>`;

    const trafficLight = `
      <div style="background:var(--panel);border:1px solid var(--border);border-left:4px solid ${riskCol};border-radius:4px;padding:20px 24px;margin-bottom:20px;display:flex;gap:24px;align-items:flex-start;flex-wrap:wrap;">
        <div style="text-align:center;min-width:80px;">
          <div style="font-size:42px;line-height:1;">${risk.label === 'GREEN' ? '🟢' : risk.label === 'YELLOW' ? '🟡' : '🔴'}</div>
          <div style="font-family:var(--mono);font-size:13px;font-weight:700;color:${riskCol};margin-top:6px;letter-spacing:0.1em;">${esc(risk.label)}</div>
          <div style="font-size:10px;color:var(--text3);margin-top:2px;">Risk score: ${risk.score}</div>
        </div>
        <div style="flex:1;min-width:180px;">
          <div style="font-size:11px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:8px;">Active Signals</div>
          ${signalRows}
        </div>
      </div>`;

    // ── Indicator cards ───────────────────────────────────────────────────────
    const cards = INDICATORS.map(ind => {
      const series = getSeries(ind.series);
      const val    = latest(series);
      const prev   = nBack(series, ind.series === 'UNRATE' ? 3 : 5);
      if (val === null) return '';

      const col    = indicatorColor(ind.series, val);
      const spark  = sparkline(chronological(series), col);
      const arrow  = trendArrow(val, prev);
      const fmtVal = formatValue(ind.series, val);
      const latestDate = series.length ? series[0].date : '';

      return `
        <div style="background:var(--panel);border:1px solid var(--border);border-radius:4px;padding:16px 18px;display:flex;gap:16px;align-items:center;">
          <div style="flex:1;min-width:0;">
            <div style="font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:var(--text3);margin-bottom:4px;">${esc(ind.label)}</div>
            <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:4px;">
              <span style="font-family:var(--mono);font-size:22px;font-weight:700;color:${col};">${esc(fmtVal)}</span>
              <span style="font-family:var(--mono);font-size:11px;">${arrow}</span>
            </div>
            <div style="font-size:10px;color:var(--text3);line-height:1.4;">${esc(ind.desc)}</div>
            <div style="font-size:10px;color:var(--text3);margin-top:3px;font-family:var(--mono);">Latest: ${esc(latestDate)}</div>
          </div>
          <div style="flex-shrink:0;">${spark}</div>
        </div>`;
    }).join('');

    this.el('fred-body').innerHTML = `
      ${trafficLight}
      <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(340px,1fr));gap:10px;margin-bottom:16px;">
        ${cards}
      </div>
      <div style="font-size:10px;color:var(--text3);text-align:right;padding:4px 2px;">
        Source: Alpha Vantage Economic Data · 6-hour cache · monthly/daily series
      </div>`;
  }
}
